import React from 'react'

import { FormikAmountField } from 'components/FormikFields'
import config from 'config'
import {
    FeatureFlagContextInterface,
    useFeatureFlagContext,
} from 'contexts/FeatureFlagContextProvider'
import AssetType from 'state/enums/AssetType'

import FormContent from '../../components/FormContent'
import {
    YearDropdown,
    MakeDropdown,
    ModelDropdown,
    VehicleDropdown,
} from './dropdowns'

interface vehicleSelectionProps {
    assetType?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: any
    vehicleNumber: number
    prefilled?: boolean
    vehicleTypeList?: []
    onWriteInModelOpen?: () => void
}

const defaultProps = {
    assetType: null,
    prefilled: false,
    vehicleTypeList: null,
}

function getDataSourceByAssetType(assetType?: string) {
    const newRvDatasource = ['LOS', 'LOS_ADMIN', 'NADA']
    const newVehicleDatasource = ['LOS_ADMIN', 'LOS']

    if (assetType === AssetType.RECREATIONAL_VEHICLE) return newRvDatasource

    return newVehicleDatasource
}

function derivePriceWriteInLabel(
    assetType?: string,
    condition?: string,
    vehicle?: {
        retail_price?: number
        wholesale_price?: number
    },
    featureFlags?: FeatureFlagContextInterface | null,
): string | undefined {
    if (!featureFlags) {
        return undefined
    }

    const missingValues: (number | null | undefined)[] = [null, 0]

    if (assetType === AssetType.RECREATIONAL_VEHICLE) {
        if (condition === 'new') {
            return featureFlags.invoicePriceForNewRv
                ? 'Invoice Price'
                : 'Sales Price'
        }

        const missingRetailPrice = missingValues.includes(vehicle?.retail_price)

        const missingWholesalePrice = missingValues.includes(
            vehicle?.wholesale_price,
        )

        if (
            condition === 'used' &&
            missingRetailPrice &&
            missingWholesalePrice
        ) {
            return 'NADA Retail Value'
        }
    }

    return undefined
}

export default function VehicleSelection({
    assetType,
    formik,
    vehicleNumber,
    prefilled,
    vehicleTypeList,
    onWriteInModelOpen,
}: vehicleSelectionProps) {
    const featureFlags = useFeatureFlagContext()
    const { values } = formik
    const { vehicle } = values
    const { condition } = vehicle

    let vehicleSelectedValue =
        vehicleNumber === 1 ? 'vehicle.primary' : 'vehicle.secondary'

    vehicleSelectedValue += '.asset_value'

    const newDataSource = getDataSourceByAssetType(assetType)

    const dataSource =
        condition === 'new' ? newDataSource : ['LOS', 'LOS_ADMIN', 'NADA']

    const labelForWriteInPrice = derivePriceWriteInLabel(
        assetType,
        condition,
        vehicle?.primary,
        featureFlags,
    )

    let tooltipForWriteInPrice

    if (labelForWriteInPrice === 'Invoice Price' && condition === 'new') {
        tooltipForWriteInPrice =
            'Invoice Price includes collateral value, discounts and freight'
    } else if (labelForWriteInPrice === 'NADA Retail Value') {
        tooltipForWriteInPrice =
            'If we are able to find a match for this RV within the JDPower database, we will use the JDPower valuation instead of this write-in amount. You will be notifed if we are able to locate a match and update the valuation.'
    }

    return (
        <>
            <FormContent gridTemplate="repeat(3, minmax(138px, 2000px))">
                <YearDropdown
                    formik={formik}
                    lenderId={config.ROADRUNNER_LENDER_ID}
                    dataSource={dataSource}
                    assetType={assetType}
                    vehicleNumber={vehicleNumber}
                    prefilled={prefilled}
                    vehicleTypeList={vehicleTypeList}
                />
                <MakeDropdown
                    formik={formik}
                    lenderId={config.ROADRUNNER_LENDER_ID}
                    dataSource={dataSource}
                    assetType={assetType}
                    vehicleNumber={vehicleNumber}
                    prefilled={prefilled}
                    vehicleTypeList={vehicleTypeList}
                />
                {!formik.values.vehicle.isTrailer && (
                    <ModelDropdown
                        formik={formik}
                        lenderId={config.ROADRUNNER_LENDER_ID}
                        dataSource={dataSource}
                        assetType={assetType}
                        vehicleNumber={vehicleNumber}
                        prefilled={prefilled}
                        vehicleTypeList={vehicleTypeList}
                        onWriteInModelOpen={onWriteInModelOpen}
                    />
                )}
            </FormContent>
            {!formik.values.vehicle.isTrailer && (
                <FormContent gridTemplate="minmax(296px, 2fr) minmax(0, .96fr)">
                    <VehicleDropdown
                        formik={formik}
                        lenderId={config.ROADRUNNER_LENDER_ID}
                        dataSource={dataSource}
                        assetType={assetType}
                        vehicleNumber={vehicleNumber}
                        prefilled={prefilled}
                    />
                </FormContent>
            )}
            <FormContent>
                {labelForWriteInPrice && (
                    <FormikAmountField
                        currencySymbol="$"
                        label={labelForWriteInPrice}
                        name={vehicleSelectedValue}
                        omitDecimals
                        placeholder="0"
                        tooltip={tooltipForWriteInPrice}
                    />
                )}
            </FormContent>
        </>
    )
}

VehicleSelection.defaultProps = defaultProps

export const testExports = {
    derivePriceWriteInLabel,
}
